import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { 
  Flame, Plus, Calendar, Clock, Users, Edit, Trash2,
  Play, Loader2, CheckCircle, XCircle, Flag, Eye, Star, Image as ImageIcon, Upload
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { PresignedImage, PresignedVideo } from '../components/PresignedMedia';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sidequest Management
const SidequestManagement = () => {
  const [sidequests, setSidequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingQuest, setEditingQuest] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    release_date: '',
    deadline_days: 7,
    max_video_length_seconds: 60,
    allow_photos: true,
    allow_videos: true
  });

  useEffect(() => {
    fetchSidequests();
  }, []);

  const fetchSidequests = async () => {
    try {
      const response = await axios.get(`${API}/sidequests?include_scheduled=true`);
      setSidequests(response.data.sidequests);
    } catch (error) {
      console.error('Failed to fetch sidequests:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      release_date: '',
      deadline_days: 7,
      max_video_length_seconds: 60,
      allow_photos: true,
      allow_videos: true
    });
    setEditingQuest(null);
  };

  const handleCreate = async () => {
    if (!formData.title || !formData.description) {
      toast.error('Title and description are required');
      return;
    }

    try {
      const payload = {
        ...formData,
        release_date: formData.release_date || null
      };
      
      if (editingQuest) {
        await axios.put(`${API}/sidequests/${editingQuest.id}`, payload);
        toast.success('Sidequest updated');
      } else {
        await axios.post(`${API}/sidequests`, payload);
        toast.success('Sidequest created');
      }
      
      setCreateModalOpen(false);
      resetForm();
      fetchSidequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save sidequest');
    }
  };

  const handleEdit = (quest) => {
    setEditingQuest(quest);
    setFormData({
      title: quest.title,
      description: quest.description,
      release_date: quest.release_date?.slice(0, 16) || '',
      deadline_days: quest.deadline_days,
      max_video_length_seconds: quest.max_video_length_seconds,
      allow_photos: quest.allow_photos,
      allow_videos: quest.allow_videos
    });
    setCreateModalOpen(true);
  };

  const handleRelease = async (questId) => {
    try {
      await axios.post(`${API}/sidequests/${questId}/release`);
      toast.success('Sidequest released!');
      fetchSidequests();
    } catch (error) {
      toast.error('Failed to release sidequest');
    }
  };

  const handleDelete = async (questId) => {
    if (!window.confirm('Delete this sidequest and all submissions?')) return;
    try {
      await axios.delete(`${API}/sidequests/${questId}`);
      toast.success('Sidequest deleted');
      fetchSidequests();
    } catch (error) {
      toast.error('Failed to delete sidequest');
    }
  };

  const toggleFeatured = async (quest) => {
    try {
      await axios.put(`${API}/sidequests/${quest.id}`, { is_featured: !quest.is_featured });
      toast.success(quest.is_featured ? 'Unfeatured' : 'Marked as Featured');
      fetchSidequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const uploadThumbnail = async (quest, file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPEG/PNG/WebP allowed');
      return;
    }
    try {
      const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const presignRes = await axios.post(`${API}/presigned-url/upload`, {
        file_type: 'sidequest-thumbnail',
        content_type: file.type,
        file_extension: extension,
        target_id: quest.id,
      });
      const { upload_url, file_path } = presignRes.data;
      await axios.put(upload_url, file, {
        headers: { 'Content-Type': file.type },
        transformRequest: [(data, headers) => {
          delete headers.Authorization; delete headers.common; return data;
        }],
      });
      await axios.put(`${API}/sidequests/${quest.id}`, { thumbnail_url: file_path });
      toast.success('Thumbnail uploaded');
      fetchSidequests();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Failed to upload thumbnail');
    }
  };

  const statusColors = {
    scheduled: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    active: 'bg-green-500/20 text-green-400 border-green-500/30',
    closed: 'bg-neutral-500/20 text-neutral-400 border-neutral-500/30'
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-red-500" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Sidequests</h1>
        <Button 
          onClick={() => { resetForm(); setCreateModalOpen(true); }}
          className="bg-orange-500 hover:bg-orange-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Sidequest
        </Button>
      </div>

      {sidequests.length === 0 ? (
        <div className="text-center py-12 bg-[#121212] rounded-2xl border border-white/10">
          <Flame className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
          <p className="text-neutral-400">No sidequests yet. Create your first one!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sidequests.map(quest => (
            <div key={quest.id} className="bg-[#121212] rounded-2xl border border-white/10 p-6" data-testid={`admin-quest-${quest.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 flex-1">
                  {/* Thumbnail */}
                  <div className="relative w-24 h-24 rounded-xl overflow-hidden bg-[#0A0A0A] border border-white/10 flex-shrink-0">
                    {quest.thumbnail_url ? (
                      <PresignedImage src={quest.thumbnail_url} alt={quest.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-neutral-600">
                        <ImageIcon className="w-6 h-6" />
                      </div>
                    )}
                    <label className="absolute inset-0 opacity-0 hover:opacity-100 bg-black/60 flex items-center justify-center cursor-pointer transition-opacity">
                      <Upload className="w-5 h-5 text-white" />
                      <input
                        type="file"
                        className="hidden"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(e) => uploadThumbnail(quest, e.target.files?.[0])}
                        data-testid={`quest-thumb-upload-${quest.id}`}
                      />
                    </label>
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge className={statusColors[quest.status]}>
                        {quest.status}
                      </Badge>
                      {quest.is_featured && (
                        <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                          <Star className="w-3 h-3 mr-1 fill-red-400" />
                          Featured
                        </Badge>
                      )}
                      <span className="text-xs text-neutral-500">
                        {quest.submission_count} submissions
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-1">{quest.title}</h3>
                    <p className="text-neutral-400 text-sm mb-3 line-clamp-2">{quest.description}</p>
                    <div className="flex flex-wrap gap-4 text-xs text-neutral-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Release: {format(parseISO(quest.release_date), 'MMM d, h:mm a')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Deadline: {format(parseISO(quest.deadline), 'MMM d, h:mm a')}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleFeatured(quest)}
                    className={`border-white/20 ${quest.is_featured ? 'text-red-400 border-red-500/40' : 'text-neutral-400'}`}
                    data-testid={`quest-toggle-featured-${quest.id}`}
                  >
                    <Star className={`w-4 h-4 mr-1 ${quest.is_featured ? 'fill-red-400' : ''}`} />
                    {quest.is_featured ? 'Unfeature' : 'Feature'}
                  </Button>
                  <div className="flex gap-2">
                    {quest.status === 'scheduled' && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleRelease(quest.id)}
                        className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Release
                      </Button>
                    )}
                    <Link to={`/sidequests/${quest.id}`}>
                      <Button variant="outline" size="sm" className="border-white/20 text-neutral-400">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </Link>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleEdit(quest)}
                      className="border-white/20 text-neutral-400"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDelete(quest.id)}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingQuest ? 'Edit Sidequest' : 'Create New Sidequest'}
            </DialogTitle>
            <DialogDescription className="text-neutral-400">
              {editingQuest ? 'Update the sidequest details' : 'Set up a new weekly challenge for the community'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-white">Title</Label>
              <Input 
                value={formData.title}
                onChange={(e) => setFormData(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g., Cold Start Video"
                className="bg-neutral-900 border-white/10 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-white">Description</Label>
              <Textarea 
                value={formData.description}
                onChange={(e) => setFormData(f => ({ ...f, description: e.target.value }))}
                placeholder="Describe the challenge..."
                className="bg-neutral-900 border-white/10 text-white mt-1"
                rows={3}
              />
            </div>

            <div>
              <Label className="text-white">Release Date/Time (leave blank for immediate)</Label>
              <Input 
                type="datetime-local"
                value={formData.release_date}
                onChange={(e) => setFormData(f => ({ ...f, release_date: e.target.value }))}
                className="bg-neutral-900 border-white/10 text-white mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Deadline (days after release)</Label>
                <Input 
                  type="number"
                  min="1"
                  value={formData.deadline_days}
                  onChange={(e) => setFormData(f => ({ ...f, deadline_days: parseInt(e.target.value) }))}
                  className="bg-neutral-900 border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white">Max Video Length (seconds)</Label>
                <Input 
                  type="number"
                  min="5"
                  value={formData.max_video_length_seconds}
                  onChange={(e) => setFormData(f => ({ ...f, max_video_length_seconds: parseInt(e.target.value) }))}
                  className="bg-neutral-900 border-white/10 text-white mt-1"
                />
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex items-center gap-2">
                <Switch 
                  checked={formData.allow_photos}
                  onCheckedChange={(checked) => setFormData(f => ({ ...f, allow_photos: checked }))}
                />
                <Label className="text-white">Allow Photos</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch 
                  checked={formData.allow_videos}
                  onCheckedChange={(checked) => setFormData(f => ({ ...f, allow_videos: checked }))}
                />
                <Label className="text-white">Allow Videos</Label>
              </div>
            </div>

            <Button 
              onClick={handleCreate}
              className="w-full bg-orange-500 hover:bg-orange-600 rounded-full"
            >
              {editingQuest ? 'Update Sidequest' : 'Create Sidequest'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Moderation Queue
const ModerationQueue = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/moderation/submissions`);
      setSubmissions(response.data.submissions);
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleModerate = async (submissionId, status) => {
    try {
      await axios.put(`${API}/moderation/submissions/${submissionId}`, { status });
      toast.success(`Submission ${status}`);
      fetchSubmissions();
    } catch (error) {
      toast.error('Failed to moderate submission');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-red-500" /></div>;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-white">Moderation Queue</h1>

      {submissions.length === 0 ? (
        <div className="text-center py-12 bg-[#121212] rounded-2xl border border-white/10">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <p className="text-neutral-400">No submissions pending review</p>
        </div>
      ) : (
        <div className="space-y-4">
          {submissions.map(sub => (
            <div key={sub.id} className="bg-[#121212] rounded-2xl border border-white/10 p-6">
              <div className="flex gap-6">
                {/* Media Preview */}
                <div className="w-48 h-32 rounded-xl overflow-hidden bg-black flex-shrink-0">
                  {sub.media_type === 'video' ? (
                    <PresignedVideo 
                      src={sub.media_urls[0]}
                      className="w-full h-full object-contain"
                      controls
                    />
                  ) : (
                    <PresignedImage 
                      src={sub.media_urls[0]}
                      alt="Submission"
                      className="w-full h-full object-contain"
                    />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                      <Flag className="w-3 h-3 mr-1" />
                      Reported
                    </Badge>
                    <span className="text-neutral-400 text-sm">by {sub.anonymous_username}</span>
                  </div>
                  
                  {sub.caption && (
                    <p className="text-neutral-300 text-sm mb-3">{sub.caption}</p>
                  )}

                  {/* Reports */}
                  <div className="bg-red-500/10 rounded-lg p-3 mb-4">
                    <p className="text-xs text-red-400 font-medium mb-1">Reports ({sub.reports?.length || 0}):</p>
                    {sub.reports?.map((report, idx) => (
                      <p key={idx} className="text-xs text-neutral-400">
                        • {report.reason} {report.details && `- ${report.details}`}
                      </p>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => handleModerate(sub.id, 'approved')}
                      className="bg-green-500 hover:bg-green-600"
                      size="sm"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                    <Button 
                      onClick={() => handleModerate(sub.id, 'rejected')}
                      variant="outline"
                      size="sm"
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Admin Sidequests Component
export default function AdminSidequestsPage() {
  const location = useLocation();

  return (
    <div className="min-h-screen bg-[#0A0A0A] px-4 sm:px-6 py-8">
      <div className="max-w-5xl mx-auto">
        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-[#121212] rounded-xl mb-8">
          <Link
            to="/admin/sidequests"
            className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium text-center transition-all
              ${location.pathname === '/admin/sidequests' ? 'bg-orange-500 text-white' : 'text-neutral-400 hover:text-white'}
            `}
          >
            <Flame className="w-4 h-4 inline mr-2" />
            Manage Quests
          </Link>
          <Link
            to="/admin/sidequests/moderation"
            className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium text-center transition-all
              ${location.pathname === '/admin/sidequests/moderation' ? 'bg-orange-500 text-white' : 'text-neutral-400 hover:text-white'}
            `}
          >
            <Flag className="w-4 h-4 inline mr-2" />
            Moderation
          </Link>
        </div>

        <Routes>
          <Route index element={<SidequestManagement />} />
          <Route path="moderation" element={<ModerationQueue />} />
        </Routes>
      </div>
    </div>
  );
}
